import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'
import { I18nextProvider } from 'react-i18next'
import { App } from '@/App.tsx'
import { i18n } from '@/lib/i18n/index.ts'
import './styles/globals.css'

// Dev-only: let a device without DevTools (a phone over LAN / a tunnel) seed the
// bearer token via `?token=…`. It's written to the same localStorage key the API
// client reads (`ludus.token`), then stripped from the URL so it isn't bookmarked
// or left in history. The `import.meta.env.DEV` guard dead-code-eliminates this
// whole block from production builds.
if (import.meta.env.DEV) {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')
  if (token) {
    localStorage.setItem('ludus.token', token)
    params.delete('token')
    const qs = params.toString()
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash,
    )
  }
}

/**
 * A **data** router with one catch-all route, rather than `<BrowserRouter>`.
 *
 * The routes themselves still live in `App.tsx` as descendant `<Routes>`; what
 * changes is which router serves them, and the reason is `useBlocker` — React
 * Router only offers it under a data router, and the flow canvas's
 * unsaved-changes guard (D9) is the first thing in the console that has edits
 * worth refusing to lose. A guard built any other way would only cover the
 * links the guarded page happens to render, and the sidebar is not one of them.
 *
 * **Created below the token strip, not above it.** `createBrowserRouter`
 * snapshots `history.location` into its initial state at construction, so a
 * router built first would capture a URL that still carried `?token=…` — the
 * very thing the block above exists to remove from the URL. `<BrowserRouter>`
 * read the location during render, i.e. after the strip, which is why the order
 * did not matter before and does now.
 */
const router = createBrowserRouter([{ path: '*', element: <App /> }])

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <RouterProvider router={router} />
    </I18nextProvider>
  </StrictMode>,
)
